import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { CalendarOffIcon, CancelAppointmentIcon } from '@/components/atoms/icons';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import React, { useEffect, useRef } from 'react';
import { Modal, StyleSheet, View } from 'react-native';

export type ReservationCancelDialogPhase = 'confirm' | 'success';

export interface ReservationCanceledDialogProps {
    visible: boolean;
    /** `confirm` = Yes/No prompt; `success` = auto-dismiss result. Default `success`. */
    phase?: ReservationCancelDialogPhase;
    confirmTitle?: string;
    confirmMessage?: string;
    noText?: string;
    yesText?: string;
    /** Success title; defaults to translated "Reservation Canceled" */
    title?: string;
    /** Success description */
    message?: string;
    /** Auto-close delay in ms while `phase` is `success`; defaults to 2000 */
    autoCloseDelayMs?: number;
    onNo?: () => void;
    onYes?: () => void;
    onAutoClose: () => void;
}

export const ReservationCanceledDialog: React.FC<
    Readonly<ReservationCanceledDialogProps>
> = ({
    visible,
    phase = 'success',
    confirmTitle,
    confirmMessage,
    noText,
    yesText,
    title,
    message,
    autoCloseDelayMs = 2000,
    onNo,
    onYes,
    onAutoClose,
}) => {
    const colors = useUIColors();
    const { t } = useTranslation();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Callers pass onAutoClose inline, so it's a new function every parent
    // render. Keeping it out of the timer effect's deps (read via ref instead)
    // stops each re-render — loader closing, reservation state clearing,
    // upcoming sync — from restarting the countdown, which kept the success
    // popup up far longer than autoCloseDelayMs (or indefinitely).
    const onAutoCloseRef = useRef(onAutoClose);
    onAutoCloseRef.current = onAutoClose;
    const isSuccess = phase === 'success';
    const resolvedConfirmTitle =
        confirmTitle ?? t('reserveUnit.cancelReservation');
    const resolvedConfirmMessage =
        confirmMessage ?? t('reserveUnit.cancelReservationConfirmMessage');
    const resolvedTitle = title ?? t('reserveUnit.reservationCanceledTitle');
    const resolvedMessage =
        message ?? t('reserveUnit.reservationCanceledMessage');
    const successViewTrackedRef = useRef(false);

    useEffect(() => {
        if (!visible || !isSuccess) {
            successViewTrackedRef.current = false;
        }
    }, [visible, isSuccess]);

    useEffect(() => {
        if (!visible || !isSuccess || successViewTrackedRef.current) {
            return;
        }
        successViewTrackedRef.current = true;
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.RESERVE,
            actionName: ALMETLAA_ACTION.RESERVE.RESERVATION_CANCELED_DIALOG_VIEW,
            eventType: 'view',
        });
    }, [visible, isSuccess]);

    useEffect(() => {
        if (!visible || !isSuccess) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            onAutoCloseRef.current();
        }, autoCloseDelayMs);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [visible, isSuccess, autoCloseDelayMs]);

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={isSuccess ? onAutoClose : onNo}
        >
            <View style={styles.overlay}>
                <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
                    <View style={styles.iconContainer}>
                        {isSuccess ? (
                            <CalendarOffIcon size={48} color={colors.fontPrimary} />
                        ) : (
                            <CancelAppointmentIcon size={48} color={colors.fontPrimary} />
                        )}
                    </View>

                    <Typography
                        variant="headline"
                        color="primary"
                        style={styles.title}
                    >
                        {isSuccess ? resolvedTitle : resolvedConfirmTitle}
                    </Typography>

                    <Typography
                        variant="body"
                        color="secondary"
                        style={styles.message}
                    >
                        {isSuccess ? resolvedMessage : resolvedConfirmMessage}
                    </Typography>

                    {!isSuccess ? (
                        <View style={styles.buttonRow}>
                            <Button
                                title={noText ?? t('common.no')}
                                variant="outline"
                                onPress={() => {
                                    trackCatalogEvent({
                                        screen: ALMETLAA_SCREEN.RESERVE,
                                        actionName: ALMETLAA_ACTION.RESERVE.CANCEL_DIALOG_NO,
                                        eventType: 'action',
                                    });
                                    onNo?.();
                                }}
                                style={styles.button}
                            />
                            <Button
                                title={yesText ?? t('common.yes')}
                                variant="primary"
                                onPress={onYes}
                                style={styles.button}
                            />
                        </View>
                    ) : null}
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    container: {
        borderRadius: 16,
        paddingTop: 32,
        paddingBottom: 24,
        paddingHorizontal: 24,
        width: '100%',
        maxWidth: 340,
        alignItems: 'center',
        gap: 16,
    },
    iconContainer: {
        marginBottom: 4,
    },
    title: {
        textAlign: 'center',
        width: '100%',
    },
    message: {
        textAlign: 'center',
        fontSize: 14,
        lineHeight: 21,
        width: '100%',
    },
    buttonRow: {
        flexDirection: 'row',
        gap: 16,
        width: '100%',
        marginTop: 4,
    },
    button: {
        flex: 1,
        height: 48,
    },
});

export default ReservationCanceledDialog;
